import { resolveProductImage } from '@eliadmin/core/src/utils/image-alt-utils';
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { GeoRadius } from '@eliadmin/core/src/api/api-types';
import {
  buildValuesWhere,
  type ProductAttributeFilter,
} from '@eliadmin/core/src/api/elastic-filter';
import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';
import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';
import type {
  ProductDetailData,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import {
  storeApiResources,
  type ApiSort,
  type ApiSortContext,
  type ApiWhere,
} from '@eliadmin/core/src/api/store-list-request';
import type { CategoryStore, SelectedValue } from '@eliadmin/core/src/stores/category-store';
import type { CustomAttributeValue } from '@eliadmin/core/src/stores/variant-store';
import { findSelectedVariant } from '@eliadmin/core/src/utils/variant-utils';

export function isSellable(
  product: Product | ProductDetailData | VariantsEntity,
  quantityToBuy: number,
) {
  if (
    (product.quantity > 0 && quantityToBuy <= product.quantity) ||
    product.availability?.sellable ||
    product.unavailability?.sellable
  )
    return true;
  return false;
}

export type QuantityRulesSource = {
  min_quantity?: number | null;
  min_quantity_multiple?: boolean | null;
  custom_quantity?: number | null;
  unit?: string | null;
};

export type QuantityRulesOptions = {
  useCustomQuantity?: boolean;
  useBalUnitAsMinMultiple?: boolean;
};

export function getProductQuantityRules(product?: any, variant?: any): QuantityRulesSource | null {
  if (!variant) {
    return product ?? null;
  }

  return {
    ...product,
    ...variant,
    min_quantity: variant.min_quantity ?? product?.min_quantity ?? null,
    min_quantity_multiple: variant.min_quantity_multiple ?? product?.min_quantity_multiple ?? null,
    custom_quantity: variant.custom_quantity ?? product?.custom_quantity ?? null,
    unit: variant.unit ?? product?.unit ?? null,
  };
}

export function getProductMinQuantity(source?: any) {
  return typeof source?.min_quantity === 'number' && source.min_quantity > 0
    ? source.min_quantity
    : 1;
}

export function getProductQuantityStep(source?: any, options: QuantityRulesOptions = {}) {
  const customQuantity =
    typeof source?.custom_quantity === 'number' && source.custom_quantity > 0
      ? source.custom_quantity
      : 1;

  if (options.useCustomQuantity && customQuantity > 1) {
    return customQuantity;
  }

  if (
    source?.min_quantity_multiple ||
    (options.useBalUnitAsMinMultiple && source?.unit === 'bal')
  ) {
    return getProductMinQuantity(source);
  }

  return 1;
}

export function normalizeProductQuantity(
  quantity: number,
  source?: any,
  options: QuantityRulesOptions = {},
) {
  const minQuantity = getProductMinQuantity(source);
  const quantityOrMin = quantity > 0 ? quantity : minQuantity;
  const clampedQuantity = Math.max(quantityOrMin, minQuantity);
  const step = getProductQuantityStep(source, options);

  if (step <= 1) {
    return clampedQuantity;
  }

  return Math.max(step, Math.ceil(clampedQuantity / step) * step);
}

export function normalizeProductQuantityDown(
  quantity: number,
  source?: any,
  options: QuantityRulesOptions = {},
) {
  const minQuantity = getProductMinQuantity(source);
  const quantityOrMin = quantity > 0 ? quantity : minQuantity;
  const step = getProductQuantityStep(source, options);

  if (step <= 1) {
    return Math.max(quantityOrMin, minQuantity);
  }

  return Math.max(minQuantity, Math.floor(quantityOrMin / step) * step);
}

export function getProductImage(
  templateSettings: TemplateSettings['data'],
  product: Product,
  filterVariants: VariantsEntity[],
) {
  return resolveProductImage(templateSettings, product, filterVariants);
}
export function getProductUrl(product: Product, selectedValues: SelectedValue[]) {
  const searchParams = new URLSearchParams();
  const result = `/${product.slug}`;

  const selectedVariant = product.variants?.length
    ? findSelectedVariant(product.variants, selectedValues)
    : null;

  if (!selectedVariant?.id) {
    return result;
  }

  searchParams.set('vid', String(selectedVariant.id));

  return `${result}?${searchParams.toString()}`;
}

export function getProductFilterVariants(product: Product, filter: CategoryStore['filter']) {
  const filterVariants: VariantsEntity[] = [];
  filter.attributes.forEach(attribute => {
    const filterVariant = product.variants?.find(variant => {
      return variant.values?.find(value => {
        return value.attribute_id === attribute.id && attribute.values.includes(value.id);
      });
    });

    if (filterVariant) {
      filterVariants.push(filterVariant);
    }
  });
  return filterVariants;
}

export type ProductOrder = 'asc' | 'desc' | null;
export type ProductOrderBy =
  | 'id'
  | 'price'
  | 'sells'
  | 'position'
  | 'views'
  | 'tags_created_at'
  | 'tags_expires_at'
  | null;

export type SearchFields = {
  name: boolean;
  description: boolean;
  'name.partial': boolean;
  short_description: boolean;
  code: boolean;
  ean: boolean;
  ext_code: boolean;
};

export type BuildProductQueryOptions = {
  categories?: (string | number)[];
  manufacturers?: (string | number)[];
  suppliers?: (string | number)[];
  page?: string | number;
  results?: number;
  discounted?: boolean;
  tags?: (string | number)[];
  order?: ProductOrder;
  orderBy?: ProductOrderBy;
  sort?: ApiSort<typeof storeApiResources.storeProducts>;
  sortContext?: ApiSortContext<typeof storeApiResources.storeProducts>;
  search?: string;
  /** attributes */
  values?: (string | number)[];
  attributes?: readonly ProductAttributeFilter[] | null;
  availabilities?: (string | number)[];
  price_from?: number;
  price_to?: number;
  geoRadius?: GeoRadius;
  code?: string;
  search_fields?: SearchFields;
  variant_images?: boolean;
  showAttributes?: boolean;
  showCategories?: boolean;
};

export function buildProductsQuery(opts: BuildProductQueryOptions = {}): FetchProductQuery {
  const result: FetchProductQuery = {};

  const ids = (values?: (string | number)[]) =>
    values
      ?.map(Number)
      .filter(value => Number.isFinite(value) && value !== 0)
      .sort((a, b) => a - b) ?? [];
  const manufacturers = ids(opts.manufacturers);
  const suppliers = ids(opts.suppliers);
  const categories = ids(opts.categories);
  const tags = ids(opts.tags);
  const values = ids(opts.values);
  const availabilities = ids(opts.availabilities);
  const where: ApiWhere<typeof storeApiResources.storeProducts> = {
    ...(manufacturers.length ? { manufacturers: { in: manufacturers } } : {}),
    ...(suppliers.length ? { suppliers: { in: suppliers } } : {}),
    ...(categories.length ? { categoriesId: { in: categories } } : {}),
    ...(tags.length ? { tags: { in: tags } } : {}),
    ...(values.length ? { values: { in: values } } : {}),
    ...(availabilities.length ? { activeAvailabilityObjectId: { in: availabilities } } : {}),
    ...(opts.discounted ? { discounted: { equals: true } } : {}),
    ...(opts.code ? { code: { equals: opts.code } } : {}),
    ...(opts.geoRadius
      ? {
          geoRadius: {
            equals: {
              latitude: opts.geoRadius.latitude,
              longitude: opts.geoRadius.longitude,
              radiusMeters: opts.geoRadius.meters,
            },
          },
        }
      : {}),
  };
  const attributeGroups = buildValuesWhere(opts.attributes);
  const conditions = [...(Object.keys(where).length ? [where] : []), ...attributeGroups];
  if (conditions.length)
    result.where = conditions.length === 1 ? conditions[0] : { and: conditions };

  result.page = opts.page ? Number(opts.page) : 1;

  if (opts.order && opts.orderBy) {
    result.order = opts.order;
  }

  if (opts.orderBy) {
    const legacyTagSort = opts.orderBy === 'tags_created_at' || opts.orderBy === 'tags_expires_at';
    result.orderBy =
      legacyTagSort && opts.sort === undefined && opts.sortContext === undefined
        ? 'id'
        : opts.orderBy;
  }

  if (opts.sort !== undefined) result.sort = opts.sort;
  if (opts.sortContext !== undefined) result.sortContext = opts.sortContext;

  if (opts.results && opts.results > 0) {
    result.results = opts.results;
  }

  if (opts.search) {
    result.search = opts.search;
  }

  if (opts.price_from && Number.isFinite(opts.price_from)) {
    result.price_from = opts.price_from;
  }

  if (opts.price_to && Number.isFinite(opts.price_to)) {
    result.price_to = opts.price_to;
  }

  if (opts.showCategories) {
    result.categories = true;
  }

  if (opts.showAttributes) {
    result.attributes = true;
  }

  result.variant_images = opts.variant_images !== false;

  if (opts.search_fields) {
    result['search_fields[]'] = (
      Object.entries(opts.search_fields) as [keyof SearchFields, boolean][]
    )
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
  }

  return result;
}

// TODO: variants, price, attributes, etc
export function parseProductSearchParams(searchParams: URLSearchParams): BuildProductQueryOptions {
  const result: BuildProductQueryOptions = {};

  const categories = searchParams.getAll('pCategory');
  const manufacturers = searchParams.getAll('manufacturer');
  const page = searchParams.get('page');
  const results = searchParams.get('results');
  const order = searchParams.get('order');
  const discounted = searchParams.get('discounted');
  const tags = searchParams.getAll('tag');
  const orderBy = searchParams.get('orderBy');
  const search = searchParams.get('search');
  const attrs = searchParams.getAll('attrs');
  const geoRadius = searchParams.get('geoRadius');
  const searchFieldsParams = [
    ...searchParams.getAll('search_fields'),
    ...searchParams.getAll('search_fields[]'),
  ];

  if (categories.length) {
    result.categories = categories.map(Number).filter(a => !isNaN(a));
  }

  if (manufacturers.length) {
    result.manufacturers = manufacturers.map(Number).filter(a => !isNaN(a));
  }

  if (attrs.length) {
    result.values = attrs
      .map(attr => {
        const [_, valueId] = attr.split(',');
        return Number(valueId);
      })
      .filter(a => !isNaN(a));
  }

  if (page) {
    result.page = page;
  }

  if (results && !isNaN(Number(results))) {
    result.results = Number(results);
  }

  if (order && ['asc', 'desc'].includes(order)) {
    result.order = order as 'asc' | 'desc';
  }

  if (orderBy && ['id', 'price', 'sells', 'position', 'views'].includes(orderBy)) {
    result.orderBy = orderBy as 'id' | 'price' | 'sells' | 'position' | 'views';
  }

  if (discounted && ['true', 'false'].includes(discounted)) {
    result.discounted = discounted === 'true';
  }

  if (tags.length) {
    result.tags = tags.map(Number).filter(a => !isNaN(a));
  }

  if (search) {
    result.search = search;
  }

  if (geoRadius) {
    const [latitude, longitude, meters] = geoRadius.split(',').map(Number);
    result.geoRadius = { latitude, longitude, meters };
  }

  if (searchFieldsParams.length) {
    const allowedSearchFields: (keyof SearchFields)[] = [
      'name',
      'description',
      'name.partial',
      'short_description',
      'code',
      'ean',
      'ext_code',
    ];
    const search_fields: Partial<SearchFields> = {};
    for (const key of searchFieldsParams) {
      if (allowedSearchFields.includes(key as keyof SearchFields)) {
        search_fields[key as keyof SearchFields] = true;
      }
    }
    result.search_fields = search_fields as SearchFields;
  }

  return result;
}

export const hasDimensions = (attributes?: Product['attributes']) => {
  return attributes?.some(attribute => attribute.type === 'range') ?? false;
};

export const getCustomValuesMultiplied = (customValues: CustomAttributeValue[]) => {
  return customValues.reduce((acc, value) => acc * parseFloat(value.value.toString()), 1);
};
